
































































































import { RootState } from '@/store/store';
import {
  FHIRDefaultParameter,
  FHIRListValue
} from '@/store/modules/fhir/types/fhir.types';
import { Component, Vue } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import TableActionDropdown from '@/components/TableActionDropdown.vue';
import EditParameterConfigurationModal from './components/EditParameterConfigurationModal.vue';

type DefaultAccess = 'ALL_CLIENTS' | 'MIXED' | 'CONFIGURED';

interface SearchParameter {
  name: string;
  key: string;
  type: string;
  maxCardinality?: number;
  defaultCardinality?: number;
  defaultAccess: DefaultAccess;
}

const ACCESS_MAP: Record<string, DefaultAccess> = {
  all_clients: 'ALL_CLIENTS',
  mixed: 'MIXED',
  configured: 'CONFIGURED'
};

const ACCESS_MAP_REVERSE: Record<DefaultAccess, string> = {
  ALL_CLIENTS: 'all_clients',
  MIXED: 'mixed',
  CONFIGURED: 'configured'
};

const TYPE_MAP: Record<string, string> = {
  integer: 'Integer',
  string: 'String',
  date: 'Date',
  checklist: 'Checklist',
  radioList: 'Radio List'
};

@Component({
  components: { TableActionDropdown, EditParameterConfigurationModal }
})
export default class SearchParametersTab extends Vue {
  public searchQuery = '';

  @Action('fhir/getDefaultParameters')
  getDefaultParameters!: () => void;

  @Action('fhir/updateDefaultParameter')
  updateDefaultParameter!: (args: {
    id: number;
    payload: {
      accessChosen: string;
      defaultCardinality?: number;
      maxCardinality?: number;
    };
  }) => void;

  @Action('fhir/updateDefaultParameterListValues')
  updateDefaultParameterListValues!: (args: {
    id: number;
    listValues: Array<{ id: number; accessChosen: string }>;
  }) => void;

  @State(({ fhir }: RootState) => fhir.defaultParameters)
  rawParameters!: FHIRDefaultParameter[] | undefined;

  public mounted() {
    this.getDefaultParameters();
  }

  public openEditModal(param: SearchParameter) {
    const raw = (this.rawParameters ?? []).find((p) => p.name === param.key);
    if (!raw) return;

    this.$buefy.modal.open({
      parent: this,
      component: EditParameterConfigurationModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        paramType: raw.type,
        currentAccess: raw.accessChosen,
        defaultCardinality: raw.defaultCardinality,
        maxCardinality: raw.maxCardinality,
        listValues: (raw.listValues ?? []) as FHIRListValue[]
      },
      events: {
        save: (payload: {
          type: string;
          accessChosen?: string;
          defaultCardinality?: number;
          maxCardinality?: number;
          listValues?: Array<{ id: number; accessChosen: string }>;
        }) => {
          if (payload.type === 'list') {
            this.updateDefaultParameterListValues({
              id: raw.id,
              listValues: payload.listValues ?? []
            });
          } else {
            this.updateDefaultParameter({
              id: raw.id,
              payload: {
                accessChosen: payload.accessChosen!,
                defaultCardinality: payload.defaultCardinality,
                maxCardinality: payload.maxCardinality
              }
            });
          }
        }
      }
    });
  }

  get parameters(): SearchParameter[] {
    if (!this.rawParameters) return [];
    return this.rawParameters.map((p) => ({
      name: p.name
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
      key: p.name,
      type: TYPE_MAP[p.type] ?? p.type,
      maxCardinality: p.maxCardinality > 0 ? p.maxCardinality : undefined,
      defaultCardinality:
        p.defaultCardinality > 0 ? p.defaultCardinality : undefined,
      defaultAccess: ACCESS_MAP[p.accessChosen] ?? 'ALL_CLIENTS'
    }));
  }

  get filteredParameters(): SearchParameter[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.parameters;
    return this.parameters.filter(
      (p) => p.name.toLowerCase().includes(q) || p.key.toLowerCase().includes(q)
    );
  }
}
